import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Switch from 'antd/lib/switch';
import Upload from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';

import { axiosGetCall, axiosPostCall } from '../../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';
import { AuthContext } from '../../context/AuthProvider';
import { uploadFile, S3ImageFolder } from '../../utils/imageUploader';

const { Option } = Select;

function AddProductModel({ isOpen, callback, handleAddModelClose }) {
  const { TextArea } = Input;

  const [addForm] = Form.useForm();
  const { token } = useContext(AuthContext);

  const [fileList, setFileList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputs, setInputs] = useState([{ key: '', value: '' }]);

  const [productCategoryList, setProductCategoryList] = useState([]);

  useEffect(() => {
    getCategoryList();
  }, []);

  async function getCategoryList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_CATEGORY_LIST,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setProductCategoryList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  function closeModel() {
    // Reset form and inputs when modal is closed
    addForm.resetFields();
    setInputs([{ key: '', value: '' }]);
    setFileList([]);
    handleAddModelClose();
  }

  async function addProduct() {
    try {
      // Validate form before proceeding
      const formValues = await addForm.validateFields();
      const {
        productName,
        modelNo,
        description,
        weight,
        featured,
        gstTax,
        bodyMaterial,
        P1,
        P2,
        P3,
        R1,
        stock,
        productCategoryId,
        youtubeLink,
      } = formValues;

      // All prices are now required individually, so no need for this validation

      // Custom validation: At least one image must be uploaded
      if (fileList.length === 0) {
        message.error('At least one product image is required');
        return;
      }

      // Build attributes object with proper validation
      const data = {};
      for (let index = 0; index < inputs.length; index++) {
        const key = formValues[`key_${index}`];
        const value = formValues[`value_${index}`];

        // Only include non-empty key-value pairs
        if (key && key.trim() && value && value.trim()) {
          data[key.trim()] = value.trim();
        }
      }

      setIsLoading(true);
      const list = [];

      for (const file of fileList) {
        const data = await uploadFile(file, S3ImageFolder.Product, token);
        list.push(data);
      }
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.ADD_PRODUCT,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {
          productName: productName,
          modelNo: modelNo,
          description: description,
          productCategoryId: productCategoryId,
          weight: weight,
          gstTax: gstTax,
          bodyMaterial: bodyMaterial,
          prices: {
            P1,
            P2,
            P3,
            R1,
          },
          sku: stock,
          attribute: JSON.stringify(data),
          featured: featured,
          userName: 'hit',
          filesPaths: list,
          youtubeLink: youtubeLink,
        },
      });
      if (res?.hideErrorFromUser) {
        message.success(res.data.message);
        callback();
        closeModel();
        setIsLoading(false);
        return;
      } else {
        message.error(res?.error?.message);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error adding product:', error);

      // Handle form validation errors
      if (error.errorFields) {
        message.error('Please fill in all required fields correctly');
      } else {
        message.error(SOMETHING_WENT_WRONG);
      }
    }
  }

  const addField = () => {
    setInputs([...inputs, { key: '', value: '' }]);
  };

  const removeField = (index) => {
    if (inputs.length > 1) {
      const newInputs = [...inputs];
      newInputs.splice(index, 1);
      setInputs(newInputs);

      // Remove form field values for the deleted index
      const currentValues = addForm.getFieldsValue();
      const newFormValues = {};

      // Rebuild form values with correct indices
      newInputs.forEach((item, newIndex) => {
        if (newIndex < index) {
          // Keep existing values for fields before the deleted one
          newFormValues[`key_${newIndex}`] = currentValues[`key_${newIndex}`];
          newFormValues[`value_${newIndex}`] =
            currentValues[`value_${newIndex}`];
        } else {
          // Shift values from fields after the deleted one
          newFormValues[`key_${newIndex}`] =
            currentValues[`key_${newIndex + 1}`] || '';
          newFormValues[`value_${newIndex}`] =
            currentValues[`value_${newIndex + 1}`] || '';
        }
      });

      addForm.setFieldsValue(newFormValues);
    } else {
      // If only one field, just clear it
      setInputs([{ key: '', value: '' }]);
      addForm.setFieldsValue({
        key_0: '',
        value_0: '',
      });
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadProps = {
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      if (fileList.length < 10) {
        setFileList([...fileList, file]);
      } else {
        message.error('Max image upload limit reached');
      }
      return false;
    },
    fileList,
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={closeModel}
        closable={false}
        footer={null}
      >
        <div className='flex flex-col space-y-4'>
          <div className='font-bold text-2xl'>Add Product</div>
          <Form form={addForm}>
            <Form.Item
              label='Product Name'
              name='productName'
              rules={[
                { required: true, message: 'Please enter product name' },
                {
                  min: 2,
                  message: 'Product name must be at least 2 characters',
                },
                {
                  max: 100,
                  message: 'Product name cannot exceed 100 characters',
                },
              ]}
            >
              <Input
                name='productName'
                placeholder='Enter product name'
              />
            </Form.Item>
            <Form.Item
              label='Model Number'
              name='modelNo'
              rules={[
                { required: true, message: 'Please enter model number' },
                {
                  max: 50,
                  message: 'Model number cannot exceed 50 characters',
                },
              ]}
            >
              <Input
                name='modelNo'
                placeholder='Enter model number'
              />
            </Form.Item>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='Weight'
                name='weight'
                rules={[
                  { required: true, message: 'Please enter weight' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Weight cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='weight'
                  min={0}
                  placeholder='Enter weight'
                />
              </Form.Item>
              <Form.Item
                label='GST'
                name='gstTax'
                rules={[
                  { required: true, message: 'Please enter GST percentage' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('GST cannot be negative'),
                        );
                      }
                      if (value && value > 100) {
                        return Promise.reject(
                          new Error('GST cannot exceed 100%'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='gstTax'
                  min={0}
                  max={100}
                  placeholder='Enter GST %'
                />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='Stock'
                name='stock'
                rules={[
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Stock cannot be negative'),
                        );
                      }
                      if (value && !Number.isInteger(value)) {
                        return Promise.reject(
                          new Error('Stock must be a whole number'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='stock'
                  min={0}
                  placeholder='Enter stock quantity'
                />
              </Form.Item>
            </Row>
            <span className='my-1 font-bold text-lg'>Prices:</span>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P1'
                name='P1'
                rules={[
                  { required: true, message: 'Please enter P1 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='P1'
                  min={0}
                  placeholder='Enter P1 price'
                />
              </Form.Item>
              <Form.Item
                label='P2'
                name='P2'
                rules={[
                  { required: true, message: 'Please enter P2 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='P2'
                  min={0}
                  placeholder='Enter P2 price'
                />
              </Form.Item>
            </Row>
            <Row className='flex flex-row justify-between'>
              <Form.Item
                label='P3'
                name='P3'
                rules={[
                  { required: true, message: 'Please enter P3 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='P3'
                  min={0}
                  placeholder='Enter P3 price'
                />
              </Form.Item>
              <Form.Item
                label='R1'
                name='R1'
                rules={[
                  { required: true, message: 'Please enter R1 price' },
                  {
                    validator: (_, value) => {
                      if (value && value < 0) {
                        return Promise.reject(
                          new Error('Price cannot be negative'),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  name='R1'
                  min={0}
                  placeholder='Enter R1 price'
                />
              </Form.Item>
            </Row>
            <Form.Item
              label='Body Material'
              name='bodyMaterial'
              rules={[
                { required: true, message: 'Please enter body material' },
                {
                  max: 100,
                  message: 'Body material cannot exceed 100 characters',
                },
              ]}
            >
              <Input
                name='bodyMaterial'
                placeholder='Enter body material'
              />
            </Form.Item>
            <Form.Item
              label='Description'
              name='description'
              rules={[
                { required: true, message: 'Please enter product description' },
                {
                  max: 1000,
                  message: 'Description cannot exceed 1000 characters',
                },
              ]}
            >
              <TextArea
                name='description'
                rows={4}
                placeholder='Enter product description'
                showCount
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item
              label='Product Category'
              name='productCategoryId'
              rules={[
                { required: true, message: 'Please select a product category' },
              ]}
            >
              <Select placeholder='Select a Product Category'>
                {productCategoryList.map((category) => (
                  <Option
                    key={category.productCategoryId}
                    value={category.productCategoryId}
                  >
                    {category.categoryName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {inputs.map((item, index) => (
              <div
                className='flex flex-row justify-between items-start'
                key={`item_${index}`}
              >
                <Form.Item name={`key_${index}`}>
                  <Input placeholder='Enter key' />
                </Form.Item>
                <Form.Item name={`value_${index}`}>
                  <Input placeholder='Enter value' />
                </Form.Item>
                <Button
                  type='default'
                  danger
                  onClick={() => removeField(index)}
                >
                  <DeleteOutlined />
                </Button>
                <Button
                  type='dashed'
                  onClick={addField}
                  className='border-primary text-primary'
                >
                  <PlusOutlined />
                </Button>
              </div>
            ))}
            <Form.Item
              label='Featured'
              name='featured'
            >
              <Switch name='featured' />
            </Form.Item>
            <Form.Item
              label='Youtube Link'
              name='youtubeLink'
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value.trim()) {
                      const youtubeRegex =
                        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
                      if (!youtubeRegex.test(value)) {
                        return Promise.reject(
                          new Error('Please enter a valid YouTube URL'),
                        );
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                name='youtubeLink'
                placeholder='Enter YouTube URL'
              />
            </Form.Item>
            <Form.Item
              label='Upload'
              valuePropName='fileList'
              getValueFromEvent={normFile}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        new Error('At least one product image is required'),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Upload
                accept='image/*'
                {...uploadProps}
                multiple={false}
                maxCount={10}
              >
                <Button
                  type='dashed'
                  icon={<UploadOutlined />}
                  className='border-primary text-primary'
                >
                  Click to Upload
                </Button>
              </Upload>
            </Form.Item>
          </Form>

          <div className='flex flex-row justify-around'>
            <Button
              onClick={closeModel}
              disabled={isLoading}
              className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
            >
              Close
            </Button>
            <Button
              onClick={addProduct}
              disabled={isLoading}
              type='primary'
              className='w-36 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
            >
              Add
            </Button>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default AddProductModel;
